import _ from 'lodash';
import { getValueByPointer } from 'fast-json-patch';
import { has } from '../../utils.js';

/**
 * Performs Operational Transformation on an array of patches.
 */
class Transform {
  /**
   * Transform constructor.
   * @param {array}   patch   // The patch operations to go through transformation
   * @param {object}  state   // The current whole state
   */
  constructor(patch, state) {
    this.patch = patch;
    this.state = state;
  }

  /**
   * Start transformation. Transform every patch operation.
   */
  transform() {
    this.patch.forEach(patchOp => {
      const { state, fromState, path, from } = patchOp;

      const pState = state
        ? this.getPartialState(path)
        : { state: null, path: null };
      const pStateFrom = fromState
        ? this.getPartialState(from)
        : { state: null, path: null };

      const transformed = this.transformOp(
        patchOp,
        pState.state,
        pState.path,
        pStateFrom.state,
        pStateFrom.path
      );
      patchOp = transformed;
    });

    // Filter out any null paths
    return this.patch.filter(patchOp => patchOp.path);
  }

  /**
   * Retrieve a part of the state (could be an array or an object). If the container name is 'children' retrieve an object.
   * @param   {string} path   The path of the state
   * @return  {object}        The partial state object and the path to it { state, path }
   */
  getPartialState(path) {
    const index = path.lastIndexOf('/');
    let statePath = path.substring(0, index);

    // Check if the state is actually a tree structure
    const arrPath = statePath.split('/');
    const container = arrPath.pop();
    if (container === 'children') {
      // Get the object with all the ids
      arrPath.pop();
      statePath = arrPath.join('/');
    }

    return { state: getValueByPointer(this.state, statePath), path: statePath };
  }

  /**
   * Transforms a patch operation that has the state property in it.
   * The state should be an array with either primitive values or objects with an id property.
   * Before any transformation we need to figure out the user's intention.
   * The operation should be commutative, associative and idempotent.
   * Each state element should be either: any primitive value or an object with the id property.
   * @param   {object}  patchOp         The patch operation should contain the state property
   * @param   {array}   current         The current partial state (state of the current user)
   * @param   {string}  currentPath     The path to this partial state
   * @param   {array}   currentFrom     The current partial state (of from)
   * @param   {string}  currentFromPath The path to the from partial state
   * @returns {object}                  The transformed patch operation
   */
  transformOp(patchOp, current, currentPath, currentFrom, currentFromPath) {
    let transformed;
    const { op, path, state } = patchOp;
    if (state) {
      patchOp.path = this.transformPath(path, state);
    }

    // In case the two states are the same just apply the patchOp to the current state
    switch (op) {
      case 'add': {
        if (state) {
          transformed = this.transformAdd(patchOp, current, currentPath);
        }
        transformed = patchOp;
        break;
      }
      case 'remove': {
        if (state) {
          transformed = this.transformRemove(patchOp, current, currentPath);
        } else {
          transformed = this.transformRemoveObject(patchOp);
        }
        break;
      }
      case 'move': {
        transformed = this.transformMove(
          patchOp,
          current,
          currentPath,
          currentFrom,
          currentFromPath
        );
        break;
      }
      default:
        transformed = patchOp;
    }

    // State is now useless after the transform
    delete transformed.state;
    delete transformed.fromState;
    return transformed;
  }

  /**
   * Find an element path in the given state.
   * If the state is array the path will contain only the index. E.g. '/5'
   * If the state is object the path will contain the whole path. E.g. '/3344/children/7'
   * @param   {*}             element   The element to search
   * @param   {array|object}  state     The state object
   * @returns {string|null}             The path of the element in the given state
   */
  getElementPath(element, state) {
    let path = null;
    if (_.isArray(state)) {
      const index = state.findIndex(el => _.isEqual(el, element));
      if (index !== -1) path = `/${index}`;
    } else if (_.isPlainObject(state)) {
      Object.values(state).forEach(value => {
        if (has(value, 'children')) {
          const { id, children } = value;
          const arrIndex = children.findIndex(el => _.isEqual(el, element));
          if (arrIndex !== -1) path = `/${id}/children/${arrIndex}`;
        }
      });
    }
    return path;
  }

  /**
   * Find an element in the given path for the specified state. Assumes that an element exists.
   * If the state is array search by index simply.
   * If the state is an object search the whole path.
   * @param   {string}        path    The path to the element
   * @param   {array|object}  state   The state object
   * @returns {*}                     The element (could be a single value or a full object)
   */
  getElement(path, state) {
    const arrState = this.getArrayState(path, state);
    return arrState[path.split('/').pop()];
  }

  /**
   * Transform a remove operation
   * @param   {Object}  patchOp     The patch operation { op, path, state }
   * @param   {array}   current     The current partial state - to be compared with patchOp.state
   * @param   {string}  currentPath The partial state path
   * @returns {Object}              The transformed patch operation (different path) { op, path, value }
   */
  transformRemove(patchOp, current, currentPath) {
    const { path, state } = patchOp;

    // In case the current state is the same with the given one nothing needs to be changed
    if (_.isEqual(state, this.getArrayState(path, current))) return patchOp;

    // Find the element that the user wants removed from his state
    const element = this.getElement(path, state);
    const elementPath = this.getElementPath(element, current);

    // Modify the operation's path to point to the element in the current state (if not removed already)
    patchOp.path = elementPath ? currentPath + elementPath : null;

    return patchOp;
  }

  /**
   * Transform a remove object operation
   * @param   {Object} patchOp    The patch operation { op, path }
   * @returns {Object}
   */
  transformRemoveObject(patchOp) {
    const { path } = patchOp;

    // Make sure the object exists in the current state before allowing removal
    const value = getValueByPointer(this.state, path);
    if (!value) {
      patchOp.path = null;
    }
    return patchOp;
  }

  /**
   * Transform an add operation.
   * @param   {Object}  patchOp     The patch operation { op, path, value, state }
   * @param   {array}   current     The current partial state - to be compared with patchOp.state
   * @param   {string}  currentPath The partial state path
   * @return  {Object}              The transformed patch operation (different path) { op, path, value, state }
   */
  transformAdd(patchOp, current) {
    const { path, value, state } = patchOp;

    // In case the current state is the same with the given one nothing needs to be changed
    if (_.isEqual(state, this.getArrayState(path, current))) return patchOp;

    const arrPath = path.split('/');

    // Get the last part of the patch operation path (the index)
    const index = arrPath.pop();
    arrPath.pop();
    arrPath.pop();

    // Store the first part of the patch operation path (no index)
    const rest = path.substr(0, path.length - index.length);

    // In case the target container does not exist abort the operation
    const arrCurrent = this.getArrayState(path, current);
    if (arrCurrent === null) {
      patchOp.path = null;
      return patchOp;
    }

    // Position the new element
    let newIndex = this.getElementIndex(
      parseInt(index, 10),
      state,
      arrCurrent,
      value
    );
    // Check the current element on the position that is going to be added
    newIndex = this.adjustIndex(newIndex, value, state, arrCurrent);
    patchOp.path = rest + newIndex;

    return patchOp;
  }

  /**
   * Transform a move operation.
   * @param   {Object}  patchOp     The patch operation { op, path, from, state, fromState }
   * @param   {Object}  current     The current partial state - to be compared with patchOp.state
   * @param   {string}  currentPath The path to the current state
   * @param   {array}   fromCurrent The current partial state (from) - to be compared with patchOp.fromState
   * @return  {Object}              The transformed patch operation (different path) { op, path, from, state, fromState }
   */
  transformMove(patchOp, current, currentPath, fromCurrent, fromCurrentPath) {
    const { path, from, state, fromState } = patchOp;

    // Find the element that the user wants to move
    const elem = this.getElement(from, fromState);
    const currentFromPath = this.getElementPath(elem, fromCurrent);
    // Could not find the element in current state - abort operation
    if (!currentFromPath) {
      patchOp.path = null;
      return patchOp;
    }
    // Modify the from path
    patchOp.from = fromCurrentPath + currentFromPath;

    const arrPath = path.split('/');
    const index = arrPath.pop();
    arrPath.pop();
    arrPath.pop();

    // Store the first part of the patch operation path (no index)
    const rest = path.substr(0, path.length - index.length);

    // In case the target container does not exist abort the operation
    const arrCurrent = this.getArrayState(path, current);
    if (arrCurrent === null) {
      patchOp.path = null;
      return patchOp;
    }

    let newIndex = this.getElementIndex(
      parseInt(index, 10),
      state,
      arrCurrent,
      elem
    );
    newIndex = this.adjustIndex(newIndex, elem, state, arrCurrent);

    patchOp.path = rest + newIndex;
    return patchOp;
  }

  /**
   * Transforms the given path in case it contains a '-' in the end
   * @param   {string}  path
   * @param   {array}   state   The state at the path
   * @returns {string}          The path with the length instead of a '-'
   */
  transformPath(path, state) {
    const arrPath = path.split('/');
    const index = arrPath.pop();
    if (index === '-') {
      arrPath.push(state.length);
      path = arrPath.join('/');
    }
    return path;
  }

  /**
   * Find the part of the state that is an array at the specified path
   * @param   {string}          path
   * @param   {array|Object}    state
   * @returns {array}                   The state itself if it is an array or the array part of it
   */
  getArrayState(path, state) {
    if (_.isArray(state)) return state;
    if (_.isPlainObject(state)) {
      const arrPath = path.split('/');
      arrPath.pop();
      arrPath.pop();
      const container = arrPath.pop();

      // In case the path does not correspond to the current state
      if (!has(state, container) || !has(state[container], 'children')) {
        return null;
      }

      return state[container].children;
    }
    return null;
  }

  /**
   * Find the index to position an element in the current state given its index in the transmitted state.
   * The index is determined by finding the previous element in the transmitted state and positioning the element
   * immediately after it in the current state.
   * @param {number}  index
   * @param {array}   state
   * @param {array}   current
   * @param {*}       element
   * @returns {number}
   */
  getElementIndex(index, state, current, element) {
    // Find the target of the operation
    if (index === 0) {
      return 0;
    }
    // Check if the element to be positioned is actually found before the index
    const elemIndex = current.findIndex(el => _.isEqual(el, element));
    // Find the first previous element in the state that exists in the current state
    let targetIndex = index;
    let newIndex = -1;
    // It will not be removed during move
    const putBefore = elemIndex !== -1 && elemIndex < targetIndex;
    if (!putBefore) {
      targetIndex -= 1;
    }
    do {
      const prevElement = state[targetIndex];
      newIndex = current.findIndex(el => _.isEqual(el, prevElement));
      targetIndex -= 1;
    } while (targetIndex > 0 && newIndex === -1);
    // No previous element is found in the current state - add this element to position 0
    if (newIndex === -1) {
      return 0;
    }
    if (!putBefore) {
      newIndex += 1;
    }
    return newIndex;
  }

  /**
   * Adjusts the index of an element if in the given index exists an element that
   * a) is smaller in comparison and
   * b) does not exist in the current state
   * @param   {number}  index
   * @param   {*}       element
   * @param   {array}   state
   * @param   {array}   current
   * @returns {number}
   */
  adjustIndex(index, element, state, current) {
    let found = false;
    let isLess = false;
    do {
      // Get the element at the specified index from the current state
      const currentElement = current[index];
      // Compare it with the given element - is it smaller?
      isLess = this.compareElements(currentElement, element);
      // Check if it also exists in the original (transmitted) state
      found = state.some(el => _.isEqual(el, currentElement));
      // If it does not exist and it is smaller move the index
      if (!found && isLess) {
        index += 1;
      }
    } while (index < current.length && !found && isLess);
    return index;
  }

  /**
   * Compares two elements. Their type should be either a primitive type or an object with the id property set.
   * @param   {*} elem1
   * @param   {*} elem2
   * @returns {boolean} True if elem1 < elem2 (or elem1.id < elem2.id) otherwise false
   */
  compareElements(elem1, elem2) {
    if (_.isPlainObject(elem1) && _.isPlainObject(elem2)) {
      return elem1.id < elem2.id;
    }
    return elem1 < elem2;
  }
}

/**
 * Transforms a patch.
 * @param {array} patch
 * @param {array} state   The whole current state
 */
export function transform(patch, state) {
  const tr = new Transform(patch, state);
  return tr.transform();
}

/**
 * Filter only data operations
 * @param {array} patch
 */
export function filterData(patch) {
  return patch.filter(patchOp => patchOp.path.startsWith('/data/'));
}
